/**
 * Registro de fechas del pedido de cliente (CEO, 26/10/2026): cada vez que se
 * reprograma una fecha (ETD, ETA, entrega estimada o solicitada), del pedido o
 * de una línea, queda la anterior tachada y la nueva detrás, y cuántas veces
 * ha cambiado. Se reconstruye del log de modificaciones (pedido_cliente_
 * modificaciones y pedidos_modificaciones de los pedidos a proveedor), que ya
 * guarda el valor anterior y el nuevo de cada cambio: no hace falta otra tabla.
 */

export const CAMPOS_FECHA = ['etd', 'eta', 'fecha_entrega_estimada', 'fecha_entrega_solicitada'] as const;
export type CampoFecha = (typeof CAMPOS_FECHA)[number];

export const ETIQUETA_CAMPO: Record<CampoFecha, string> = {
  etd: 'ETD',
  eta: 'ETA',
  fecha_entrega_estimada: 'Entrega estimada',
  fecha_entrega_solicitada: 'Entrega solicitada',
};

export type ModificacionLog = {
  fecha_hora: string;
  usuario_email: string | null;
  target_table: string;
  target_id: string;
  accion: string;
  cambios: Record<string, unknown> | null;
};

export type ValorFecha = {
  /** YYYY-MM-DD o null si se vació. */
  valor: string | null;
  /** Cuándo se puso este valor; null = valor inicial (anterior al primer cambio registrado). */
  fecha_hora: string | null;
  usuario_email: string | null;
  /** 'proveedor' si vino del pedido a proveedor; 'cabecera' si lo aplicó la cabecera a la línea. */
  via: 'proveedor' | 'cabecera' | null;
};

export type HistorialFecha = {
  /** null = cabecera del pedido; si no, id de la línea. */
  linea_id: string | null;
  campo: CampoFecha;
  valores: ValorFecha[];
  /** Veces que se ha cambiado una fecha que ya tenía valor (reprogramaciones). */
  cambios: number;
};

const DIA_MADRID = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit' });

/** Fecha del log a YYYY-MM-DD. El log guarda tanto 'YYYY-MM-DD' como ISO con hora (Date serializado). */
export function aDia(v: unknown): string | null {
  if (v == null || v === '') return null;
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : DIA_MADRID.format(d);
}

// Campo del log de pedidos a proveedor → campo de la línea del pedido de cliente.
const CAMPO_PROVEEDOR: Record<string, CampoFecha> = { eta: 'eta', fecha_estimada_llegada: 'eta', etd: 'etd' };

export function registroFechas(
  mods: ReadonlyArray<ModificacionLog>,
  /** id del pedido a proveedor → id de la línea del pedido de cliente. */
  lineaDePedidoProveedor: ReadonlyMap<string, string>,
): HistorialFecha[] {
  const orden = [...mods].sort((a, b) => a.fecha_hora.localeCompare(b.fecha_hora));
  const mapa = new Map<string, HistorialFecha>();
  const historial = (linea_id: string | null, campo: CampoFecha) => {
    const k = `${linea_id ?? 'pedido'}|${campo}`;
    let h = mapa.get(k);
    if (!h) { h = { linea_id, campo, valores: [], cambios: 0 }; mapa.set(k, h); }
    return h;
  };
  const anotar = (h: HistorialFecha, antes: unknown, despues: unknown, m: ModificacionLog, via: ValorFecha['via']) => {
    const a = aDia(antes);
    const d = aDia(despues);
    const ultimo = h.valores.at(-1);
    if (!ultimo && a) h.valores.push({ valor: a, fecha_hora: null, usuario_email: null, via: null });
    if ((h.valores.at(-1)?.valor ?? null) === d) return; // mismo valor: no es un cambio
    if (h.valores.length > 0 && h.valores.at(-1)!.valor) h.cambios++;
    h.valores.push({ valor: d, fecha_hora: m.fecha_hora, usuario_email: m.usuario_email, via });
  };

  for (const m of orden) {
    const c = m.cambios ?? {};
    if (m.target_table === 'pedidos_cliente' || m.target_table === 'pedidos_cliente_lineas') {
      const linea = m.target_table === 'pedidos_cliente' ? null : m.target_id;
      const via = c.__desde_cabecera ? 'cabecera' : null;
      for (const campo of CAMPOS_FECHA) {
        if (!(campo in c)) continue;
        const v = c[campo];
        // Alta (insert): el log guarda la fila entera; el valor es el inicial.
        if (m.accion === 'insert') { if (aDia(v)) anotar(historial(linea, campo), null, v, m, via); continue; }
        if (v && typeof v === 'object' && ('antes' in v || 'despues' in v)) {
          const { antes, despues } = v as { antes?: unknown; despues?: unknown };
          anotar(historial(linea, campo), antes, despues, m, via);
        }
      }
    } else if (m.target_table === 'pedidos') {
      const linea = lineaDePedidoProveedor.get(m.target_id);
      if (!linea) continue;
      for (const [clave, campo] of Object.entries(CAMPO_PROVEEDOR)) {
        const v = c[clave];
        if (v && typeof v === 'object' && ('antes' in v || 'despues' in v)) {
          const { antes, despues } = v as { antes?: unknown; despues?: unknown };
          anotar(historial(linea, campo), antes, despues, m, 'proveedor');
        }
      }
    }
  }
  return [...mapa.values()].filter((h) => h.valores.length > 0);
}
